import { useState, Fragment } from 'react';
import { classNames } from 'primereact/utils';
import { SidebarMenuItem } from '.';
import { SidebarMenuProps } from '../../../models/sidebar/sidebar';
import { useAuth } from '@mezzodevelopment/mezzo-ui/auth';

const SidebarMenu = (props: { className?: string; minimized: boolean }): JSX.Element => {
  const { className, minimized } = props;

  const [currentMenu, setCurrentMenu] = useState<string>('');

  const { user } = useAuth();

  const selectMenu = (id: string): void => {
    if (currentMenu === id) {
      setCurrentMenu('');
    } else {
      setCurrentMenu(id);
    }
  };

  const isAuthorized = (authorizedGroups?: Array<string>) => {
    if (!authorizedGroups) {
      return true;
    }
    const userGroups: string[] = user?.roles || [];
    return userGroups && authorizedGroups.some((g) => userGroups.includes(g));
  };

  const content = [
    {
      menuItems: [
        {
          label: 'Dashboard',
          to: '/dashboard',
          icon: 'pi-chart-bar',
        },
        {
          label: 'Credit',
          icon: 'pi-book',
          submenuItems: [
            {
              label: 'Emulator',
              to: '/credit/emulator',
            },
            {
              label: 'Accounts',
              to: '/credit/accounts',
            },
            {
              label: 'Transactions',
              to: '/credit/transactions',
            },
          ],
        },
        {
          label: 'Underwrite',
          to: '/underwrite',
          icon: 'pi-book',
        },
        {
          label: 'Billing',
          to: '/billing',
          icon: 'pi-money-bill',
        },
        {
          label: 'Fee Transactions Logs',
          to: '/fee-tracing',
          icon: 'pi-search',
        },
        {
          label: 'VoX Transaction Logs',
          to: '/vox-tracing',
          icon: 'pi-search',
        },
      ],
    },
    {
      header: 'Admin',
      authorizedGroups: ['SuperAdmin', 'Admin'],
      menuItems: [
        {
          label: 'Tenants',
          to: '/tenants',
          icon: 'pi-user',
          authorizedGroups: ['SuperAdmin'],
        },
        {
          label: 'User Accounts',
          to: '/user-accounts',
          icon: 'pi-users',
          authorizedGroups: ['SuperAdmin', 'Admin'],
        },
        {
          label: 'Organizational Units',
          to: '/org-units',
          icon: 'pi-sitemap',
          authorizedGroups: ['SuperAdmin', 'Admin'],
        },
        {
          label: 'Connection Settings',
          to: '/connection-settings',
          icon: 'pi-link',
          authorizedGroups: ['SuperAdmin', 'Admin'],
        },
        {
          label: 'Waterfalls',
          to: '/waterfalls',
          icon: 'pi-sitemap',
          authorizedGroups: ['SuperAdmin', 'Admin'],
        },
        {
          label: 'Fees',
          icon: 'pi-dollar',
          authorizedGroups: ['SuperAdmin', 'Admin'],
          submenuItems: [
            {
              label: 'Fee Configurations',
              to: '/fees',
            },
            {
              label: 'HUD Lines',
              to: '/fees/hud-lines',
            },
          ],
        },
        {
          label: 'Credit Reporting Agencies',
          to: '/credit-reporting-agencies',
          icon: 'pi-user',
          authorizedGroups: ['SuperAdmin'],
        },
        {
          label: 'Webhook Subscriptions',
          to: '/webhook-subscriptions',
          icon: 'pi-link',
          authorizedGroups: ['SuperAdmin'],
        },
        {
          label: 'Email Templates',
          icon: 'pi-email',
          authorizedGroups: ['SuperAdmin'],
          submenuItems: [
            {
              label: 'Lock Request Email',
              to: '/email-template/lock-request',
            },
          ],
        },
      ],
    },
  ];

  return (
    <section className={classNames('overflow-y-auto nav-scrollbar p-4', className)}>
      {content.map((ele, idx) => {
        if (!isAuthorized(ele.authorizedGroups)) return null;

        const section = ele.menuItems;
        const sectionIdx = idx;
        return (
          <Fragment key={idx}>
            {ele.header && (
              // eslint-disable-next-line no-octal-escape
              <h2 className="uppercase font-bold overflow-x-hidden my-3 empty:before:content-['\200B']">
                {!minimized && ele.header}
              </h2>
            )}
            <>
              {section.map((ele: SidebarMenuProps, idx) => {
                if (ele.authorizedGroups && !isAuthorized(ele.authorizedGroups)) {
                  return null;
                }

                return (
                  <SidebarMenuItem
                    key={idx}
                    id={`${sectionIdx}_${idx}`}
                    minimized={minimized}
                    label={ele.label}
                    to={ele.to}
                    submenuItems={ele.submenuItems}
                    icon={ele.icon}
                    currentMenu={currentMenu}
                    selectMenu={selectMenu}
                  />
                );
              })}
            </>
          </Fragment>
        );
      })}
    </section>
  );
};

export default SidebarMenu;
